import { ref } from 'vue';
import { debounce } from 'lodash-es';
import { SelectedTagData } from '@/common/components/templates/selectTargetTagWindow/selectTargetTagWindow.type';

import {
  ExcludeKeyword,
  ExcludeKeywordSegmentedType,
  ExcludeTagType,
} from './excludeKeywordButton.types';
import { TagRequest, TagRequestTagType } from '@/api-client/data/model';

export const useExcludePatternButton = (
  excludeKeyword: ExcludeKeyword = { excludeKeywordList: [], excludeKeywordSegmentedType: 'OR' },
) => {
  const excludeKeywordValueList = ref<string[]>(excludeKeyword.excludeKeywordList ?? []);
  const excludeKeywordSegmentedType = ref<ExcludeKeywordSegmentedType>(
    excludeKeyword.excludeKeywordSegmentedType ?? 'OR',
  );

  let callBack: () => void;

  const convertExcludeTagType = (segmentedType: ExcludeKeywordSegmentedType): ExcludeTagType => {
    return segmentedType === 'OR' ? 'exclude_pattern' : 'exclude_and_pattern';
  };

  const onClickSaveExcludeKeyword = ({
    keywordList,
    segmentedType,
  }: {
    keywordList: string[];
    segmentedType: ExcludeKeywordSegmentedType;
  }) => {
    excludeKeywordValueList.value = keywordList.filter((pattern) => pattern.trim() !== '');
    excludeKeywordSegmentedType.value = segmentedType;
    callBack?.();
  };

  const onClickRemoveExcludeKeyword = ({ index }: { index: number }) => {
    excludeKeywordValueList.value = excludeKeywordValueList.value.filter((_, i) => i !== index);
    callBack?.();
  };

  // excludeTag 저장시 사용 하는 함수  ex) application;exclude_pattern;abc123 형식으로 저장
  const convertExcludeKeywordToParams = (
    segmentedType: ExcludeKeywordSegmentedType = excludeKeywordSegmentedType.value,
    excludeKeywordList: string[] = excludeKeywordValueList.value,
    categories: string[] | string,
  ) => {
    if (!segmentedType || !excludeKeywordList?.length) {
      return [];
    }
    return Array.isArray(categories)
      ? categories.flatMap((category) => {
          return (
            excludeKeywordList?.map(
              (keyword) => `${category};${convertExcludeTagType(segmentedType)};${keyword}`,
            ) ?? []
          );
        })
      : (excludeKeywordList?.map(
          (keyword) => `${categories};${convertExcludeTagType(segmentedType)};${keyword}`,
        ) ?? []);
  };

  // tag: string application;exclude_pattern;abc,123 -> abc,123 추출
  const extractExcludeKeywords = (excludeTag: string): ExcludeKeyword => {
    const parts = excludeTag.split(/(?:exclude_pattern|exclude_and_pattern);/);
    const [matchType] = excludeTag.match(/(?:exclude_pattern|exclude_and_pattern)/) ?? [];
    const excludeKeywordType: ExcludeKeywordSegmentedType =
      matchType === 'exclude_and_pattern' ? 'AND' : 'OR';

    const extractedParts = parts.slice(1);

    return {
      excludeKeywordList: extractedParts
        .map((part, index) => {
          if (index === extractedParts.length - 1) {
            return part;
          }
          const lastCommaIndex = part.lastIndexOf(',');
          if (lastCommaIndex !== -1) {
            return part.substring(0, lastCommaIndex);
          }
          return part;
        })
        .filter((value) => value.length > 0),
      excludeKeywordSegmentedType: excludeKeywordType,
    };
  };

  const updateExcludeKeyword = (callback: () => void) => {
    callBack = debounce(callback, 300);
  };

  // 태그 카테고리 변환 함수
  /**
   *
   * @param selectedTags
   * @param excludeKeywordList
   * @param segmentedType
   * @param category 타겟 카테고리를 넣어줘야함
   * @returns
   */
  const convertSelectedTagsWithExcludeKeywords = <T extends SelectedTagData[] | TagRequest[]>(
    selectedTags: T,
    excludeKeywordList: string[],
    segmentedType: ExcludeKeywordSegmentedType,
    category: string,
  ): T extends TagRequest[] ? TagRequest[] : SelectedTagData[] => {
    const isSelectedTagData = (tag: SelectedTagData | TagRequest): tag is SelectedTagData => {
      return 'groupTagId' in tag && tag.groupTagId !== undefined;
    };
    if (excludeKeywordList.length === 0 || selectedTags.length === 0) {
      return [...selectedTags] as T extends TagRequest[] ? TagRequest[] : SelectedTagData[];
    }

    const tag = selectedTags[0];
    if (isSelectedTagData(tag)) {
      // SelectedTagData 타입 >> 여기는 타겟 팝업 태그 포맷
      return [
        ...selectedTags,
        ...excludeKeywordList.map((value) => ({
          ...tag,
          tagType: convertExcludeTagType(segmentedType),
          groupTagId: category,
          tagId: value,
        })),
      ] as T extends TagRequest[] ? TagRequest[] : SelectedTagData[];
    }
    // TagRequest 타입 >> 여기는 알람 프리뷰 태그 포맷 왜 같은 포맷인데 리퀘스트 변수가 다를까..?
    return [
      ...selectedTags,
      {
        ...tag,
        tagType: convertExcludeTagType(segmentedType) as TagRequestTagType,
        tagKey: category,
        tagValue: excludeKeywordList,
      },
    ] as T extends TagRequest[] ? TagRequest[] : SelectedTagData[];
  };

  // 멀티 카테고리 대비 (target-select-v2 컴포넌트 에서 사용)
  // TODO: 만약 추후 카테고리 별 excludeKeyword 를 넣어줘야 한다면 excludeKeywordValueList 단순 배열이아닌 Map 형태로 전환 해야함
  const simpleConvertSelectedTagsWithExcludeKeywords = (
    excludeKeywordList: string[],
    segmentedType: ExcludeKeywordSegmentedType,
    categories: string[] = [],
  ): TagRequest[] => {
    if (excludeKeywordList.length === 0 || categories.length === 0) {
      return [];
    }
    return categories.map((category) => ({
      tagType: convertExcludeTagType(segmentedType) as TagRequestTagType,
      tagKey: category,
      tagValue: excludeKeywordList,
    }));
  };

  const initExcludeKeyword = () => {
    excludeKeywordValueList.value = [];
    excludeKeywordSegmentedType.value = 'OR';
  };

  return {
    excludeKeywordValueList,
    excludeKeywordSegmentedType,

    onClickSaveExcludeKeyword,
    onClickRemoveExcludeKeyword,
    convertExcludeKeywordToParams,
    extractExcludeKeywords,
    updateExcludeKeyword,
    convertExcludeTagType,
    convertSelectedTagsWithExcludeKeywords,
    simpleConvertSelectedTagsWithExcludeKeywords,
    initExcludeKeyword,
  };
};
